import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import Loader from '../../../components/loader'
import ToggleSwitch from '../../../components/toggle-switch'
import classnames from 'classnames'
import { FiWifiOff } from 'react-icons/fi'
import WifiConfigTab from '../0x0001/wificonfig.tab'
import PasswordInput from '../../../components/password-input'
import WifiList from '../../../components/wifilist'
import { getClientWifiScanResult, IWifiScanResult } from '../../../api/0x0001/wificonfig'
import {
    getWifiStatus,
    getWifiConfig,
    enableWifi,
    connectWifi,
    forgetWifi,
    WifiStatusResponse,
} from '../../../api/0x0005/wifi'

type Props = {
    clientId: string
    /** Firmware version from static traits, e.g. "v26.4.0". Used to pick legacy vs sub-opcode path fast. */
    firmwareVersion?: string
}

type State = {
    loading: boolean
    /** True = device runs firmware < v26.4.0 → legacy opcode 8/9/10 UI */
    legacy: boolean
    /** True = the server denied access (403) — show a permission error, NOT legacy mode */
    permissionDenied: boolean
    status?: WifiStatusResponse
    ssid: string
    password: string
    connecting: boolean
    connectingStart: number
    /** Networks found by the last scan (opcode 10) */
    scanResult?: IWifiScanResult[]
    scanning: boolean
}

const POLL_INTERVAL_MS = 2000
const CONNECT_TIMEOUT_MS = 30000

/** LTCB220 WiFi remote management (sub-opcodes 99/100/240-243) requires firmware v26.4.0+. */
function isLegacyFirmware(version?: string): boolean {
    if (!version) return false // unknown → probe the new path, fall back on failure
    const match = version.trim().match(/^v?(\d+)\.(\d+)\.(\d+)/)
    if (!match) return false
    const [major, minor] = [Number(match[1]), Number(match[2])]
    return major < 26 || (major === 26 && minor < 4)
}

/** True when the device is fully connected (has an IP). */
function isWifiConnected(status: WifiStatusResponse): boolean {
    return status.state === 2 || (status.connected === 1 && !!status.ipAddress)
}

/** Signal strength as 1-4 bars, mirroring the mobile app. */
function wifiRssiLevel(rssi?: number): number {
    if (rssi === undefined || rssi === null) return 4
    if (rssi >= -55) return 4
    if (rssi >= -67) return 3
    if (rssi >= -75) return 2
    if (rssi >= -85) return 1
    return 1
}

/** Main status line — mirrors the mobile app's WiFi tab. */
function wifiStateLabel(status: WifiStatusResponse, connecting: boolean): string {
    if (connecting || status.state === 1) return getString('wifi.state_connecting')
    if (isWifiConnected(status)) return getString('wifi.connected_to') + ' ' + (status.ssid || '')
    if (status.state === 3) return getString('wifi.state_failed')
    return getString('wifi.state_idle')
}

/** Small 4-bar WiFi signal indicator (strength-based, like the mobile app). */
function SignalBars({ level, className }: { level: number; className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            {[1, 2, 3, 4].map(bar => {
                const active = bar <= level
                const x = 3 + (bar - 1) * 4.5
                const h = 4 + bar * 3
                return <rect key={bar} x={x} y={24 - h - 1} width="3" height={h} rx="1" opacity={active ? 1 : 0.25} />
            })}
        </svg>
    )
}

function lastErrorLabel(reason?: number, msg?: string): string {
    if (!reason || reason === 0) return ''
    if (reason === 202) return getString('wifi.wrong_password')
    if (reason === 201) return getString('wifi.network_not_found')
    return msg || `${getString('wifi.last_error')} #${reason}`
}

export default class WifiTab extends Component<Props, State> {
    state: State = {
        loading: true,
        legacy: isLegacyFirmware(this.props.firmwareVersion),
        permissionDenied: false,
        ssid: '',
        password: '',
        connecting: false,
        connectingStart: 0,
        scanning: false,
    }

    pollTimer?: ReturnType<typeof setInterval>

    componentDidMount() {
        if (!this.state.legacy) {
            this.loadStatus()
        }
    }

    /** Old firmware (or any device that doesn't answer sub-opcode 99) → legacy opcode 8/9/10 UI. */
    enterLegacyMode = () => {
        this.stopPolling()
        if (!this.state.legacy) {
            this.setState({ legacy: true, loading: false, connecting: false })
        }
    }

    /** Server denied access (403) — show an error, do NOT treat it as old firmware. */
    handleAccessDenied = (error: any) => {
        this.stopPolling()
        if (!this.state.permissionDenied) {
            this.setState({ permissionDenied: true, loading: false, connecting: false })
        }
        console.warn('[WiFi Tab] Permission denied:', error?.message)
    }

    componentWillUnmount() {
        this.stopPolling()
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = undefined
        }
    }

    loadStatus = async () => {
        try {
            const [status, config] = await Promise.all([
                getWifiStatus(this.props.clientId),
                // Read the device's saved SSID/password (opcode 8). Non-fatal: don't break the
                // status load if this read fails.
                getWifiConfig(this.props.clientId).catch(() => undefined),
            ])
            const next: Partial<State> = { status, loading: false }
            // Prefill SSID + password from the device's saved station config (same as the mobile app),
            // but only while the user hasn't typed anything and we're not mid-connect.
            if (!this.state.ssid.trim() && !this.state.password && !this.state.connecting) {
                next.ssid = config?.staSsid || status.ssid || status.savedSsid || ''
                next.password = config?.staPass || ''
            }
            this.setState(next as State)
        } catch (error: any) {
            if (error?.status === 403) {
                this.handleAccessDenied(error)
                return
            }
            // No response to sub-opcode 99 → old firmware (< v26.4.0). Fall back to opcode 8/9/10.
            console.warn('[WiFi Tab] Status request failed — falling back to legacy WiFi config:', error?.message)
            this.enterLegacyMode()
        }
    }

    isWifiEnabled = () => {
        const { status } = this.state
        if (!status) return false
        return status.enabled === 1 || status.connected === 1 || !!status.ipAddress
    }

    handleEnableToggle = async () => {
        if (!this.state.status) return
        const newEnabledState = !this.isWifiEnabled()
        try {
            await enableWifi(this.props.clientId, newEnabledState)
            toast.success(newEnabledState ? getString('wifi.wifi_enabled') : getString('wifi.wifi_disabled'))
            await this.loadStatus()
        } catch (error: any) {
            toast.error(error.message || getString('wifi.error'))
        }
    }

    handleConnect = async () => {
        const { ssid, password, connecting } = this.state
        if (connecting) return
        if (!ssid.trim()) {
            toast.error(getString('wifi.ssid_required'))
            return
        }
        try {
            this.setState({ connecting: true, connectingStart: Date.now() })
            await connectWifi(this.props.clientId, ssid.trim(), password)
            toast.info(getString('wifi.connect_accepted'))
            // Poll status until the connection settles (success or failure)
            await this.pollUntilSettled()
        } catch (error: any) {
            toast.error(error.message || getString('wifi.connect_failed'))
            this.setState({ connecting: false })
        }
    }

    pollUntilSettled = async () => {
        this.stopPolling()
        this.pollTimer = setInterval(async () => {
            try {
                const status = await getWifiStatus(this.props.clientId)
                this.setState({ status })
                const elapsed = Date.now() - this.state.connectingStart

                if (status.state === 2 && status.connected === 1 && status.ipAddress && status.ipAddress !== '0.0.0.0') {
                    // Connected
                    this.stopPolling()
                    this.setState({ connecting: false })
                    toast.success(getString('wifi.connected_to') + ' ' + (status.ssid || ''))
                } else if (status.state === 3) {
                    // Failed — surface the reason
                    this.stopPolling()
                    this.setState({ connecting: false })
                    toast.error(lastErrorLabel(status.lastErrorReason, status.lastErrorMsg) || getString('wifi.connect_failed'))
                } else if (elapsed >= CONNECT_TIMEOUT_MS) {
                    this.stopPolling()
                    this.setState({ connecting: false })
                    toast.warn(getString('wifi.timeout'))
                }
            } catch (error: any) {
                this.stopPolling()
                this.setState({ connecting: false })
                if (error?.status === 403) {
                    this.handleAccessDenied(error)
                    return
                }
                console.warn('[WiFi Tab] Poll failed — falling back to legacy WiFi config:', error?.message)
                this.enterLegacyMode()
            }
        }, POLL_INTERVAL_MS)
    }

    handleForget = async () => {
        const confirmed = window.confirm(getString('wifi.forget_confirm'))
        if (!confirmed) return
        try {
            await forgetWifi(this.props.clientId)
            toast.success(getString('wifi.forgot'))
            await this.loadStatus()
        } catch (error: any) {
            toast.error(error.message || getString('wifi.error'))
        }
    }

    /** Scan for nearby networks (base opcode 10 — same as the mobile app). */
    scanNetworks = async () => {
        if (this.state.scanning) return
        this.setState({ scanning: true, scanResult: undefined })
        try {
            const result = (await getClientWifiScanResult(this.props.clientId)).data
            this.setState({ scanResult: result, scanning: false })
        } catch (error: any) {
            this.setState({ scanning: false })
            console.warn('[WiFi Tab] Scan failed:', error?.message)
            toast.error(error.message || getString('wifi.error'))
        }
    }

    /** Tapping a scanned network fills the SSID and clears the password (like the mobile app). */
    handleSelectNetwork = (ssid: string) => {
        const previous = this.state.ssid.trim()
        const next = ssid.trim()
        this.setState({
            ssid,
            password: previous !== next ? '' : this.state.password,
        })
    }

    render() {
        const { loading, legacy, permissionDenied, status, ssid, password, connecting, scanning, scanResult } = this.state

        // Server denied access — not a firmware issue.
        if (permissionDenied) {
            return (
                <div className="p-6">
                    <div className="bg-red-50 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg p-4">
                        <p className="text-sm text-red-800 dark:text-red-200">{getString('wifi.permission_denied')}</p>
                    </div>
                </div>
            )
        }

        // Legacy firmware (< v26.4.0): reuse the classic opcode 8/9/10 WiFi config UI.
        if (legacy) {
            return (
                <div className="p-6">
                    <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4 mb-4">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">{getString('wifi.legacy_mode')}</p>
                    </div>
                    <WifiConfigTab clientId={this.props.clientId} />
                </div>
            )
        }

        if (loading || !status) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Loader />
                </div>
            )
        }

        const savedNetwork = status.hasSavedNetwork === 1

        return (
            <div className="p-6">

                {/* Enable/Disable Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('wifi.enable')}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {this.isWifiEnabled() ? getString('wifi.enabled') : getString('wifi.disabled')}
                            </p>
                        </div>
                        <ToggleSwitch
                            checked={this.isWifiEnabled()}
                            onChange={this.handleEnableToggle}
                        />
                    </div>
                </div>

                {/* Status Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <div className="flex items-center gap-4">
                        {connecting || status.state === 1 ? (
                            <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-indigo-500 rounded-full animate-spin shrink-0" />
                        ) : isWifiConnected(status) ? (
                            <SignalBars level={wifiRssiLevel(status.rssi)} className="w-8 h-8 text-green-500 shrink-0" />
                        ) : status.state === 3 ? (
                            <FiWifiOff size={32} className="text-red-500 shrink-0" />
                        ) : (
                            <FiWifiOff size={32} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        )}
                        <div className="min-w-0 flex-grow">
                            <div className="flex items-center gap-2">
                                <span className="text-lg font-semibold text-gray-800 dark:text-white truncate">
                                    {wifiStateLabel(status, connecting)}
                                </span>
                                {isWifiConnected(status) && (
                                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block shrink-0" />
                                )}
                            </div>
                            {isWifiConnected(status) && status.ipAddress && (
                                <div className={classnames('text-sm text-gray-600 dark:text-gray-400 font-mono mt-0.5', getLocale() === 'he' && 'text-right')}>
                                    {status.ipAddress}
                                </div>
                            )}
                            {!!status.lastErrorReason && status.lastErrorReason !== 0 && (
                                <div className="text-sm text-red-600 dark:text-red-400 mt-0.5">
                                    {lastErrorLabel(status.lastErrorReason, status.lastErrorMsg)}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={this.loadStatus}
                            className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shrink-0"
                        >
                            {getString('settings.refresh')}
                        </button>
                    </div>
                </div>

                {/* Connect to Network */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wifi.connect_to_network')}</h3>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {getString('wifi.ssid')}
                        </label>
                        <input
                            className={classnames('pretty-input ltr')}
                            value={ssid}
                            onChange={(e: any) => this.setState({ ssid: e.target.value })}
                            placeholder="MyWiFi"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {getString('wifi.password')}
                        </label>
                        <PasswordInput
                            className={classnames('pretty-input ltr')}
                            value={password}
                            onChange={value => this.setState({ password: value })}
                            placeholder={getString('wifi.password_optional')}
                        />
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={this.handleConnect}
                            disabled={connecting}
                            className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            {connecting ? getString('wifi.connecting') : getString('wifi.connect')}
                        </button>
                        {savedNetwork && (
                            <button
                                onClick={this.handleForget}
                                className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                {getString('wifi.forget')}
                            </button>
                        )}
                        <button
                            onClick={this.scanNetworks}
                            disabled={scanning}
                            className="py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            {scanning ? getString('common.loading') : getString('common.scan')}
                        </button>
                    </div>

                    {/* Scan results (opcode 10) */}
                    {(scanning || scanResult) && (
                        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                            {scanning ? (
                                <Loader size={12} className="mx-auto" innerClassName="mx-auto" />
                            ) : scanResult && scanResult.length > 0 ? (
                                <WifiList
                                    items={scanResult}
                                    password={password}
                                    onPassChange={value => this.setState({ password: value })}
                                    onSsidChange={this.handleSelectNetwork}
                                />
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400">{getString('wifi.no_networks')}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}
